import React, { useRef } from "react";
import { Camera, ImagePlus, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";

const MAX = 10 * 1024 * 1024;
const OK = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

// Camera + gallery buttons for the chat composer. Calls onSelect(files[]).
export function ImageAttach({ onSelect, disabled }) {
  const { t } = useLanguage();
  const camRef = useRef(null);
  const galRef = useRef(null);

  const pick = (fileList) => {
    const list = Array.from(fileList || []);
    const valid = [];
    for (const f of list) {
      if (!OK.includes(f.type)) { toast.error(t("image_type_error")); continue; }
      if (f.size > MAX) { toast.error(t("image_size_error")); continue; }
      valid.push(f);
    }
    if (valid.length) onSelect(valid);
  };

  const btn = "flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-primary disabled:opacity-40";

  return (
    <>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
        data-testid="chat-camera-input" onChange={(e) => { pick(e.target.files); e.target.value = ""; }} />
      <input ref={galRef} type="file" accept="image/*,application/pdf" multiple className="hidden"
        data-testid="chat-gallery-input" onChange={(e) => { pick(e.target.files); e.target.value = ""; }} />
      <button type="button" disabled={disabled} onClick={() => camRef.current?.click()}
        data-testid="chat-camera-btn" aria-label={t("take_photo")} className={btn}>
        <Camera className="h-5 w-5" />
      </button>
      <button type="button" disabled={disabled} onClick={() => galRef.current?.click()}
        data-testid="chat-gallery-btn" aria-label={t("upload_image")} className={btn}>
        <ImagePlus className="h-5 w-5" />
      </button>
    </>
  );
}

// Thumbnail previews of the attached files with per-item remove.
export function ImagePreview({ files, file, onRemove, onClear }) {
  const { t } = useLanguage();
  const list = files ? files : (file ? [file] : []);
  if (!list.length) return null;
  return (
    <div className="mb-2 flex flex-wrap gap-2" data-testid="chat-image-preview">
      {list.map((f, i) => {
        const isImage = f.type.startsWith("image/");
        const url = isImage ? URL.createObjectURL(f) : null;
        return (
          <div key={i} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2">
            {isImage ? (
              <img src={url} alt={f.name} className="h-12 w-12 rounded-lg object-cover" />
            ) : (
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-white text-slate-400"><FileText className="h-6 w-6" /></span>
            )}
            <span className="max-w-[120px] truncate text-xs text-slate-600">{f.name}</span>
            <button type="button" onClick={() => (onRemove ? onRemove(i) : onClear?.())} data-testid={`chat-image-remove-${i}`} aria-label={t("remove_file")}
              className="flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700">
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
